import {
    ClockCounterClockwise,
    Factory,
    Link as LinkIcon,
    MinusCircle,
    PencilSimple,
    Plus,
} from "@phosphor-icons/react";
import {
    RequiredEvidenceForEmissionsSource,
    RequiredEvidence,
    RequiredEvidenceStatus,
} from "../../api/models/RequiredEvidence";
import { formatDateOnlyString } from "../../utils/dateTimeFormatters";
import Badge, { BadgeVariant } from "../Badge";
import IconButton from "../IconButton";
import Table, { TableHeaderConfig } from "../Table";
import { format } from "date-fns";
import { appPaths } from "../../Routes";
import TableContextMenu, { TableContextMenuOption } from "../TableContextMenu";

const isEmissionsSource = (
    requiredEvidence: RequiredEvidence | RequiredEvidenceForEmissionsSource
): requiredEvidence is RequiredEvidenceForEmissionsSource => {
    return Object.prototype.hasOwnProperty.call(
        requiredEvidence,
        "emissionsSourceName"
    );
};

const buildEvidenceHistoryPath = (
    requiredEvidence: RequiredEvidence | RequiredEvidenceForEmissionsSource
) => {
    if (isEmissionsSource(requiredEvidence)) {
        return appPaths.emissionsSourceEvidenceHistory(
            requiredEvidence.emissionsSourceId.toString(),
            requiredEvidence.evidenceTypeId.toString()
        );
    }

    return appPaths.organizationEvidenceHistory(
        requiredEvidence.evidenceTypeId.toString()
    );
};

export type RequiredEvidenceTableProps<
    T extends RequiredEvidence | RequiredEvidenceForEmissionsSource
> = {
    isLoading: boolean;
    hideLinkToEmissionsSource?: boolean;
    showEmissionsSourceColumn?: boolean;
    activityDataId?: number;
    requiredEvidence: T[];
    noEvidenceMessage: string;
    readonly?: boolean;
    orderBy?: string;
    onOrderBy?: (orderBy: string) => void;
    sortOrder?: string;
    handleLinkClick: (uri: string) => void;
    handleAddClick: (rowData: T) => void;
    handleEditClick: (rowData: T) => void;
    handleRemoveEvidenceTypeClick: (rowData: T) => void;
};

const evidenceStatusBadgeVariantMap: Record<
    RequiredEvidenceStatus,
    BadgeVariant
> = {
    Missing: "error",
    Expired: "warning",
    Expiring: "warning",
    Ok: "success",
};

const formatExpiresOn = (requiredEvidence: RequiredEvidence): string => {
    if (requiredEvidence.status === "Missing") return "Missing";

    if (requiredEvidence.status === "Expired") {
        const expiredDate = format(
            requiredEvidence.evidenceTypeCoveredUntil
                ? new Date(requiredEvidence.evidenceTypeCoveredUntil)
                : new Date(),
            "yyyy-MM-dd"
        );
        return `Expired on ${expiredDate}`;
    }

    if (requiredEvidence.status === "Expiring") {
        if ((requiredEvidence.evidenceTypeExpiresInDays ?? 0) <= 7)
            return "Expires within 7 days";
        if ((requiredEvidence.evidenceTypeExpiresInDays ?? 0) <= 30)
            return "Expires within 30 days";
        if ((requiredEvidence.evidenceTypeExpiresInDays ?? 0) <= 90)
            return "Expires within 90 days";
    }

    return "Ok";
};

const RequiredEvidenceTable = <
    T extends RequiredEvidence | RequiredEvidenceForEmissionsSource
>({
    isLoading,
    requiredEvidence,
    hideLinkToEmissionsSource,
    showEmissionsSourceColumn,
    activityDataId,
    noEvidenceMessage,
    readonly,
    orderBy,
    sortOrder,
    onOrderBy,
    handleLinkClick,
    handleAddClick,
    handleEditClick,
    handleRemoveEvidenceTypeClick,
}: RequiredEvidenceTableProps<T>) => {
    if (isLoading) {
        return <p className="py-10 text-center">Loading...</p>;
    }

    if (requiredEvidence.length === 0) {
        return (
            <div className="flex-1 flex justify-center items-center">
                <div className="flex flex-col items-center">
                    <p className="mt-2 opacity-60">{noEvidenceMessage}</p>
                </div>
            </div>
        );
    }

    const headings: TableHeaderConfig[] = [];

    if (showEmissionsSourceColumn) {
        headings.push({
            label: "Asset",
            orderByKey: "EmissionsSourceName",
        });
        headings.push({
            label: "Location",
            orderByKey: "EmissionsSourceLocation",
        });
        headings.push({
            label: "Unique ref.",
            orderByKey: "EmissionsSourceUniqueReference",
        });
    }

    headings.push({ label: "Evidence type", orderByKey: "EvidenceTypeName" });

    if (activityDataId) {
        headings.push({ label: "Evidence coverage" });
    } else {
        headings.push(
            { label: "History Status" },
            { label: "Current document status" }
        );
    }

    headings.push(
        { label: activityDataId ? "Evidence document" : "Current document" },
        { label: "" }
    );

    const buildTableContextMenuOptions = (rowData: T) => {
        const options: TableContextMenuOption[] = readonly
            ? [
                  {
                      icon: ClockCounterClockwise,
                      label: "View history",
                      to: buildEvidenceHistoryPath(rowData),
                  },
              ]
            : [
                  {
                      icon: Plus,
                      label: "Add evidence",
                      onClick: () => handleAddClick(rowData),
                  },
                  {
                      icon: ClockCounterClockwise,
                      label: "View history",
                      to: buildEvidenceHistoryPath(rowData),
                  },
                  {
                      icon: MinusCircle,
                      label: "Remove evidence type",
                      onClick: () => handleRemoveEvidenceTypeClick(rowData),
                  },
              ];

        if (isEmissionsSource(rowData) && !hideLinkToEmissionsSource) {
            options.push({
                icon: Factory,
                label: "View emissions source",
                to: appPaths.emissionsSourceDetail(
                    `${rowData.emissionsSourceId}`
                ),
            });
        }

        return options;
    };

    return (
        <Table
            headings={headings}
            data={requiredEvidence.map((expiringEvidenceItem, index) => ({
                id: index,
                ...expiringEvidenceItem,
            }))}
            className="!max-w-full !min-w-full"
            renderRowCells={(rowData) => {
                return (
                    <>
                        {showEmissionsSourceColumn &&
                            isEmissionsSource(rowData) && (
                                <>
                                    <td className="!min-w-[150px]">
                                        {rowData.emissionsSourceName}
                                    </td>
                                    <td className="!min-w-[150px]">
                                        {rowData.emissionsSourceLocation}
                                    </td>
                                    <td className="!min-w-[150px]">
                                        {rowData.emissionsSourceUniqueReference}
                                    </td>
                                </>
                            )}
                        <td className="!min-w-[150px]">
                            {rowData.evidenceTypeName}
                        </td>
                        {activityDataId && isEmissionsSource(rowData) ? (
                            <td className="!min-w-[250px] !w-[250px]">
                                <Badge
                                    variant={
                                        ["Missing", "Expired"].includes(
                                            rowData.status
                                        )
                                            ? "error"
                                            : "success"
                                    }
                                >
                                    {["Missing", "Expired"].includes(
                                        rowData.status
                                    )
                                        ? "Not covered"
                                        : "Covered"}
                                </Badge>
                            </td>
                        ) : (
                            <>
                                <td className="!min-w-[200px] !w-[200px]">
                                    <Badge
                                        variant={
                                            evidenceStatusBadgeVariantMap[
                                                rowData.isComplete
                                                    ? "Ok"
                                                    : "Missing"
                                            ]
                                        }
                                    >
                                        {rowData.isComplete
                                            ? "Complete"
                                            : "Incomplete"}
                                    </Badge>
                                </td>
                                <td className="!min-w-[250px] !w-[250px]">
                                    <Badge
                                        variant={
                                            evidenceStatusBadgeVariantMap[
                                                rowData.status
                                            ]
                                        }
                                    >
                                        {formatExpiresOn(rowData)}
                                    </Badge>
                                </td>
                            </>
                        )}
                        <td className="!min-w-[270px] !w-[270px]">
                            <div className="flex items-center">
                                {rowData.currentEvidenceDownloadUrl ? (
                                    <>
                                        {formatDateOnlyString(
                                            rowData.currentEvidenceValidFromDate
                                        )}{" "}
                                        -{" "}
                                        {formatDateOnlyString(
                                            rowData.currentEvidenceValidToDate
                                        )}
                                        <IconButton
                                            icon={LinkIcon}
                                            label="View"
                                            onClick={() =>
                                                handleLinkClick(
                                                    rowData.currentEvidenceDownloadUrl ||
                                                        ""
                                                )
                                            }
                                            className="ml-2"
                                        />
                                        {!readonly && (
                                            <IconButton
                                                icon={PencilSimple}
                                                label="Edit"
                                                onClick={() =>
                                                    handleEditClick(rowData)
                                                }
                                            />
                                        )}
                                    </>
                                ) : (
                                    <p>-</p>
                                )}
                            </div>
                        </td>
                        <td className="!min-w-[100px] !w-[100px] !overflow-visible">
                            <div className="flex justify-end gap-3 items-center">
                                <TableContextMenu
                                    options={buildTableContextMenuOptions(
                                        rowData
                                    )}
                                />
                            </div>
                        </td>
                    </>
                );
            }}
            sortOrder={sortOrder}
            orderBy={orderBy}
            onOrderBy={onOrderBy}
        />
    );
};

export default RequiredEvidenceTable;
